import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, QrCode, Users, History } from "lucide-react";
import { API_URL } from "@/utils/apiConfig";
import { TableHistoryModal } from "@/components/tables/TableHistoryModal";
import { NewMesaDialog, QRCodeDialog } from "./MesasGridDialogs";

const BACKEND_URL = API_URL;

/**
 * MesasGrid - Grid de mesas com status e ações
 */
export function MesasGrid({ tables, setTables, onSelectTable, onRefresh, restaurantSlug, customDomain, restaurant }) {
  const { t } = useTranslation();
  const { authAxios, user } = useAuth();
  const [showNewMesa, setShowNewMesa] = useState(false);
  const [showQRModal, setShowQRModal] = useState(null);
  const [historyTable, setHistoryTable] = useState(null);
  const [newName, setNewName] = useState("");
  const [newNumber, setNewNumber] = useState(tables.length + 1);
  const [newCapacity, setNewCapacity] = useState(4);
  const [loading, setLoading] = useState(false);
  const qrRef = useRef(null);

  const base = restaurantSlug || user?.restaurant_id;

  const createTable = async () => {
    setLoading(true);
    try {
      const res = await authAxios.post("/tables/", null, {
        params: {
          name: newName || `Mesa ${newNumber}`,
          number: newNumber,
          capacity: newCapacity,
        }
      });
      toast.success(t("tables.tableCreated"));
      setShowNewMesa(false);
      setNewName("");
      if (res.data && setTables) {
        setTables(prev => [...prev, res.data]);
      } else {
        onRefresh();
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : Array.isArray(d) ? d.map(x => x?.msg || String(x)).join("; ") : t("tables.errorCreatingTable"));
    }
    setLoading(false);
  };

  const getMenuUrl = (table) => {
    return customDomain 
      ? `https://${customDomain}/m/${base}?table=${table.id}`
      : `${BACKEND_URL}/m/${base}?table=${table.id}`;
  };

  const downloadQR = (table) => {
    const canvas = document.createElement("canvas");
    const size = 400;
    const padding = 40;
    canvas.width = size;
    canvas.height = size + 80;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#111";
    ctx.font = "bold 22px Arial, sans-serif";
    ctx.textAlign = "center";
    const tableName = table.name || `MESA ${String(table.number).padStart(2, "0")}`;
    ctx.fillText(tableName, size / 2, 30);
    const svgEl = document.getElementById(`qr-${table.id}`);
    if (svgEl) {
      const svgData = new XMLSerializer().serializeToString(svgEl);
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, padding, 42, size - padding * 2, size - padding * 2);
        ctx.fillStyle = "#666";
        ctx.font = "12px Arial, sans-serif";
        ctx.fillText("Escanea para hacer tu pedido", size / 2, size + 30);
        const link = document.createElement("a");
        link.download = `qr_${tableName.toLowerCase().replace(/\s+/g, "_")}.png`;
        link.href = canvas.toDataURL("image/png");
        link.click();
        toast.success(t("tables.qrDownloaded"));
      };
      img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
    }
    setShowQRModal(null);
  };

  // Compute effective status: if there's a pending amount, it's occupied regardless of stored status
  const getEffectiveStatus = (table) => {
    if (table.status === "reserved" || table.status === "dirty") return table.status;
    if (table.pending_amount > 0 || table.pending_payment_count > 0) return "occupied";
    return table.status || "available";
  };

  // Check idle time: returns 0 (normal), 1 (>30min orange), 2 (>40min red)
  const getIdleLevel = (table) => {
    const status = getEffectiveStatus(table);
    if (status !== "occupied") return 0;
    const since = table.occupied_since;
    if (!since) return 0;
    const mins = (Date.now() - new Date(since).getTime()) / 60000;
    if (mins > 40) return 2;
    if (mins > 30) return 1;
    return 0;
  };
  const isIdleWarning = (table) => getIdleLevel(table) >= 1;

  const getStatusColor = (status) => {
    switch (status) {
      case "occupied": return "bg-yellow-500";
      case "reserved": return "bg-blue-500";
      case "dirty": return "bg-red-500";
      case "available": return "bg-green-500";
      default: return "bg-green-500";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "occupied": return t("tables.statusOccupied");
      case "reserved": return t("tables.statusReserved");
      case "dirty": return t("tables.statusDirty");
      case "available": return t("tables.statusAvailable");
      default: return t("tables.statusAvailable");
    }
  };

  const getCardStyle = (status, idle) => {
    if (idle) return "border-orange-400 bg-orange-50";
    switch (status) {
      case "occupied": return "border-yellow-400 bg-yellow-50";
      case "reserved": return "border-blue-400 bg-blue-50";
      case "dirty": return "border-red-400 bg-red-50";
      default: return "border-green-400 bg-green-50 hover:border-green-500";
    }
  };

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-gray-800">Mesas ({tables.length})</h2>
        <Button
          onClick={() => setShowNewMesa(true)}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="add-table-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> {t("tables.newTable")}
        </Button>
      </div>

      {/* Grid de mesas */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
        {tables.map(table => {
          const tableName = table.name || `MESA ${String(table.number).padStart(2, "0")}`;
          const effStatus = getEffectiveStatus(table);
          const idleLevel = getIdleLevel(table);
          const idle = idleLevel >= 1;
          const veryIdle = idleLevel >= 2;
          const cardClass = veryIdle ? "border-red-500 bg-red-50" : idle ? "border-orange-400 bg-orange-50" : getCardStyle(effStatus, false).split(" ").filter(c => !c.startsWith("border-orange") && !c.startsWith("bg-orange")).join(" ");
          return (
            <button
              key={table.id}
              onClick={() => onSelectTable(table)}
              className={`relative rounded-xl border-2 p-4 text-left transition-all hover:shadow-lg active:scale-95 ${veryIdle ? "border-red-500 bg-red-50" : idle ? "border-orange-400 bg-orange-50" : getCardStyle(effStatus, false)}`}
              data-testid={`table-${table.number}`}
            >
              {/* Status dot */}
              <div className={`absolute top-3 right-3 w-3 h-3 rounded-full ${veryIdle ? "bg-red-500 animate-pulse" : idle ? "bg-orange-400 animate-pulse" : getStatusColor(effStatus)}`} />
              
              {/* QR Icon */}
              <span
                onClick={(e) => { e.stopPropagation(); setShowQRModal(table); }}
                className="absolute top-3 left-3 text-gray-400 hover:text-emerald-600 cursor-pointer"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setShowQRModal(table); }}}
              >
                <QrCode className="w-5 h-5" />
              </span>
              {/* History Icon */}
              <span
                onClick={(e) => { e.stopPropagation(); setHistoryTable(table); }}
                className="absolute bottom-3 right-3 text-gray-300 hover:text-emerald-600 cursor-pointer"
                role="button"
                tabIndex={0}
                title="Ver histórico"
                onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setHistoryTable(table); }}}
              >
                <History className="w-4 h-4" />
              </span>

              {/* Mesa info */}
              <div className="mt-6">
                <h3 className="font-bold text-gray-800 text-lg">{tableName}</h3>
                <div className="flex items-center gap-1 text-gray-500 text-sm mt-1">
                  <Users className="w-4 h-4" />
                  <span>{table.capacity || 4}</span>
                </div>
                <Badge
                  className={`mt-2 text-xs font-bold ${
                    veryIdle ? "bg-red-100 text-red-700 border-red-400" :
                    idle ? "bg-orange-100 text-orange-700 border-orange-300" :
                    effStatus === "occupied" ? "bg-yellow-100 text-yellow-700 border-yellow-300" :
                    effStatus === "reserved" ? "bg-blue-100 text-blue-700 border-blue-300" :
                    effStatus === "dirty" ? "bg-red-100 text-red-700 border-red-300" :
                    "bg-green-100 text-green-700 border-green-300"
                  }`}
                >
                  {veryIdle ? "🔴 +40min" : idle ? "⏱ +30min" : getStatusLabel(effStatus)}
                </Badge>
                {table.pending_payment_count > 0 && (
                  <p className="text-red-600 font-bold text-xs mt-1 animate-pulse" title="Pedidos aguardando pagamento">
                    ⚠ {table.pending_payment_count} aguarda pgto
                  </p>
                )}
                {table.pending_amount > 0 && (
                  <p className="text-emerald-600 font-bold mt-2">
                    {table.pending_amount.toFixed(2)}€
                  </p>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Empty state */}
      {tables.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <QrCode className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-lg font-medium">{t("tables.noTablesConfigured")}</p>
          <p className="text-sm mt-1">{t("tables.createFirstTable")}</p>
        </div>
      )}

      {/* Legend */}
      {tables.length > 0 && (
        <div className="flex items-center gap-4 mt-4 text-xs text-gray-500 border-t pt-4">
          <span className="font-medium">{t("tables.legend")}:</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-500"></span> {t("tables.statusAvailable")}</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-yellow-500"></span> {t("tables.statusOccupied")}</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-500"></span> {t("tables.statusReserved")}</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-500"></span> {t("tables.statusDirty")}</span>
        </div>
      )}

      {/* Dialog: New Mesa */}
      <NewMesaDialog
        open={showNewMesa}
        onOpenChange={setShowNewMesa}
        newName={newName}
        setNewName={setNewName}
        newNumber={newNumber}
        setNewNumber={setNewNumber}
        newCapacity={newCapacity}
        setNewCapacity={setNewCapacity}
        loading={loading}
        onCreate={createTable}
      />

      {/* Dialog: Table History */}
      <TableHistoryModal
        table={historyTable}
        open={!!historyTable}
        onClose={() => setHistoryTable(null)}
      />

      {/* Dialog: QR Code */}
      <QRCodeDialog
        showQRModal={showQRModal}
        onClose={() => setShowQRModal(null)}
        getMenuUrl={getMenuUrl}
        downloadQR={downloadQR}
      />
    </div>
  );
}
